import { useState } from 'react';

import { IconThumbDown, IconThumbUp } from '@tabler/icons-react';
import { Widget } from '@typeform/embed-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Typography } from '@cfa/react-components';
import { Button, Modal } from '@cfa/react-core';

import { useAuth } from '@/hooks/useAuth';

import {
  getSmartSearchFeedbackHiddenFields,
  SMART_SEARCH_FEEDBACK_FORM_ID,
} from './utils';

import { SmartAnswersResult } from '@/_generated/api-clients/xp';
import { ThankYou } from '@/icons';

interface FeedbackProps {
  smartAnswer?: SmartAnswersResult;
  question: string;
}

const Feedback: React.FC<FeedbackProps> = ({ smartAnswer, question }) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formKey, setFormKey] = useState<string>();
  const [feedbackResponse, setFeedbackResponse] = useState('');

  if (!smartAnswer || !auth) {
    return null;
  }

  const handleThumbClick = (thumbsUp: boolean) => {
    setFeedbackResponse(thumbsUp ? 'Thumbs Up' : 'Thumbs Down');
    setFormKey(uuidv4());
    setIsSubmitted(false);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const hiddenFields = getSmartSearchFeedbackHiddenFields({
    feedbackResponse,
    smartAnswer,
    question,
    userFullName: auth.fullName,
  });

  return (
    <>
      <div className="flex gap-4 pr-5">
        <button
          type="button"
          aria-label={t('AISearchPOC.thumbsUpFeedback')}
          onClick={() => handleThumbClick(true)}
        >
          <IconThumbUp
            aria-hidden
            className="text-brand-navy cursor-pointer transition-all hover:scale-105 active:scale-90"
          />
        </button>
        <button
          type="button"
          aria-label={t('AISearchPOC.thumbsDownFeedback')}
          onClick={() => handleThumbClick(false)}
        >
          <IconThumbDown
            aria-hidden
            className="text-brand-navy cursor-pointer transition-all hover:scale-105 active:scale-90"
          />
        </button>
      </div>
      <Modal.Overlay
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        isDismissable
        className="z-2000!"
      >
        <Modal.Dialog
          isDismissable
          className="relative! m-2! h-dvh! w-[min(480px,98vw)]! overflow-hidden! rounded-xl! sm:m-4! sm:h-[min(650px,90vh)]!"
        >
          <AnimatePresence mode="wait" initial={false}>
            {!isSubmitted ? (
              <motion.div
                key="form"
                className="absolute inset-0 overflow-hidden bg-white"
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="size-[111%] origin-top-left scale-90 sm:size-full sm:scale-100">
                  <Widget
                    style={{ height: '100%', width: '100%' }}
                    hidden={hiddenFields}
                    hideHeaders={true}
                    id={SMART_SEARCH_FEEDBACK_FORM_ID}
                    inlineOnMobile
                    key={formKey}
                    onSubmit={() => setIsSubmitted(true)}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="thank-you"
                className="absolute inset-0 flex flex-col items-center justify-center px-6 pb-20"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              >
                <ThankYou className="size-30" />
                <Typography
                  variant="h2"
                  className="mt-10! mb-8!"
                  fontWeight="medium"
                >
                  {t('Settings.thankYou')}
                </Typography>
                <Typography variant="body1" className="text-grayscale-gray4!">
                  {t('Settings.acknowledgement')}
                </Typography>
                <Button
                  className="absolute bottom-10 w-[calc(100%-3rem)]!"
                  color="secondary"
                  onPress={handleClose}
                >
                  {t('Button.close')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </Modal.Dialog>
      </Modal.Overlay>
    </>
  );
};

export default Feedback;
